/**
 * Porte d'entree du back-office de plateforme.
 *
 * Elle refuse les comptes de restaurant : voir auth/context.tsx et
 * docs/adr/0015-back-office-de-plateforme-separe.md.
 */

import { useState } from 'react';

import { ApiError } from '../api/client.js';
import { NotPlatformAdminError, useAuth } from '../auth/context.js';
import { labels } from '../labels.js';
import { Icon } from '../components/Icon.js';
import { Logo } from '../components/Logo.js';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
    } catch (err) {
      if (err instanceof NotPlatformAdminError) setError(err.message);
      else if (err instanceof ApiError && err.httpStatus === 401) setError(labels.auth.invalid);
      else setError(labels.auth.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className={styles.screen}>
      <section className={styles.brand} aria-hidden="true">
        <Logo size={40} withWordmark tone="dark" />
        <div className={styles.brandBody}>
          <h2 className={styles.brandTitle}>{labels.auth.brandTitle}</h2>
          <p className={styles.brandText}>{labels.auth.brandText}</p>
          <ul className={styles.points}>
            {labels.auth.brandPoints.map((point) => (
              <li key={point}>
                <span className={styles.pointIcon}><Icon name="check" size={16} /></span>
                {point}
              </li>
            ))}
          </ul>
        </div>
        <p className={styles.brandFoot}>{labels.app.name} · {labels.app.subtitle}</p>
      </section>

      <section className={styles.formSide}>
        <form onSubmit={submit} className={styles.form}>
          <span className={styles.mobileLogo}><Logo size={40} withWordmark /></span>
          <div>
            <h1 className="text-title-1" style={{ margin: 0 }}>{labels.auth.title}</h1>
            <p className="page-subtitle">{labels.auth.intro}</p>
          </div>

          {error && (
            <p role="alert" className="notice notice--danger" style={{ margin: 0 }}>
              <Icon name="alert" size={18} />
              {error}
            </p>
          )}

          <div className="field">
            <label htmlFor="email" className="field-label">{labels.auth.email}</label>
            <input id="email" className="input" type="email" value={email} required autoComplete="username" autoFocus
              onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="password" className="field-label">{labels.auth.password}</label>
            <input id="password" className="input" type="password" value={password} required autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)} />
          </div>

          <button type="submit" className="btn-primary btn-block btn-lg" disabled={busy}>
            {busy ? labels.auth.submitting : labels.auth.submit}
            {!busy && <Icon name="arrow-right" size={18} />}
          </button>
        </form>
      </section>
    </main>
  );
}
